
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Box,
  Typography
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/Search';

interface Language {
  code: string;
  name: string;
}

const supportedLanguages: Language[] = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'es-ES', name: 'Español (España)' },
  { code: 'es-419', name: 'Español (Latinoamérica)' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'fi', name: 'Suomi' },
  { code: 'hi', name: 'हिन्दी' },
  { code: 'sv', name: 'Svenska' },
];

const LanguageSelector: React.FC = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    handleClose();
  };

  const filteredLanguages = useMemo(() =>
    supportedLanguages.filter(lang =>
      lang.name.toLowerCase().includes(searchTerm.toLowerCase())
    ), [searchTerm]);

  const currentLanguage = supportedLanguages.find(lang => lang.code === i18n.language) || supportedLanguages[0];

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<LanguageIcon />}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          width: '100%',
          color: 'inherit',
          mb: 1,
          '& .MuiButton-startIcon': {
            mr: 1.5
          }
        }}
      >
        <Box sx={{ textAlign: 'left', overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                Language
            </Typography>
            <Typography variant="caption" sx={{ lineHeight: 1.2, opacity: 0.7, whiteSpace: 'nowrap', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {currentLanguage.name}
            </Typography>
        </Box>
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Select a Language</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search languages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1, mb: 2 }}
          />
          <List>
            {filteredLanguages.map(lang => (
              <ListItemButton
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                selected={i18n.language === lang.code}
              >
                <ListItemText primary={lang.name} />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LanguageSelector;
